import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { fetchShow } from '../../actions/appActions';
import { useSelector } from 'react-redux';
import { WelletContext } from '../../context/WelletContext';
import { getCookie } from '../../helpers/cookie';
import {
  skeletonRestaurantCard,
  skeletonRestaurantHeader,
} from '../Skeletons/SkeletonRestaurantSelector';
import EmptyActivity from '../Common/EmptyActivity/EmptyActivity';
import styles from './RestaurantList.module.css';
import ContentApp from '../ContentApp';
import RestaurantCard from '../RestaurantCard/RestaurantCard';
import AccountHeader from '../AccountHeader/AccountHeader';
import CustomerNav from '../CustomerNav/CustomerNav';
import SolidChevron from '../icons/SolidChevron';
import DataManager from '../../context/DataManager';
import { useTranslation } from 'react-i18next';
import Button from '../Common/Button/Button';

const RestaurantList = ({ history }) => {
  const { t } = useTranslation();
  const { citySelected } = useContext(DataManager);
  const cityCookie = getCookie('selectedCity');
  const userCity = useSelector((state) => state.associates.user.company.country);
  const observer = useRef();
  const pageRef = useRef(1);
  const welletContext = useContext(WelletContext);
  const app = useSelector((state) => state.app);
  const [restaurants, setRestaurants] = useState(null);
  const [loadMoreRestaurants, setLoadMoreRestaurants] = useState(false);
  const [loadingRestaurants, setLoadingRestaurants] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [cities, setCities] = useState(null);
  const [city, setCity] = useState(null);
  const canMakeReservations = useSelector((state) => state.associates.user.canMakeReservations);

  useEffect(() => {
    if (!cities) {
      getCities();
    }
  }, []);

  useEffect(() => {
    if (!restaurants && city) {
      getRestaurants(city.id);
    }
  }, [city]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const getRestaurants = async (selectedCity, page = 1, pageSize = 15) => {
    setLoadingRestaurants(true);
    try {
      const result = await fetchShow(app.language, selectedCity, page, pageSize, welletContext);
      setRestaurants((prevData) => {
        if (prevData) {
          return [...prevData, ...result.shows];
        } else {
          return result.shows;
        }
      });
      setHasMore(result.shows.length >= pageSize);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingRestaurants(false);
      setLoadMoreRestaurants(false);
    }
  };

  const getCities = async () => {
    try {
      const response = await welletContext.apis.tickets.get('/shows/cities');
      setCities(response.data);
      const defaultCity = response.data[0];
      if (citySelected) {
        setCity(response.data.find((c) => c.id === citySelected));
      } else if (cityCookie !== null && cityCookie?.trim() !== '') {
        const cookieCity = response.data.find((c) => c.id === Number(cityCookie));
        setCity(cookieCity ? cookieCity : defaultCity);
      } else {
        setCity(defaultCity);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const loadMore = () => {
    setLoadMoreRestaurants(true);
    pageRef.current += 1;
    getRestaurants(city?.id, pageRef.current);
  };

  const onRestaurantClick = (show) => {
    history.push({
      pathname: show.reservationTypes.length > 0 ? '/reservation-type' : 'reservation-restaurant',
      search: `?showId=${show.id}&showName=${show.name}`,
      state: { name: show.name },
    });
  };

  const lastElementRef = useCallback(
    (node) => {
      if (loadingRestaurants) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMore();
        }
      });
      if (node) observer.current.observe(node);
    },
    [hasMore],
  );

  return (
    <>
      <AccountHeader
        title={<div className='text-capitalize'>{t('book')}</div>}
        icon='config'
        pathName='/configuration'
      />

      <div style={{ position: 'relative', minHeight: 'calc(100vh - 60px)' }}>
        <div className={!canMakeReservations ? 'censured-content' : ''}>
          <ContentApp menu header>
            {loadingRestaurants && !restaurants ? (
              skeletonRestaurantHeader()
            ) : (
              <div className={`${styles.highlight} fade-up`}>
                <div
                  className={
                    userCity === 'US' || userCity === 'ES'
                      ? 'd-none'
                      : `d-flex mx-auto justify-content-center bold text-white align-items-center font-medium ${styles.cityContainer}`
                  }
                  style={{ width: city?.name?.length > 6 ? '70%' : '50%' }}
                  onClick={() => history.push('/city-selector')}
                >
                  {city?.name}
                  <SolidChevron size='15' color='var(--color-primary)' className='ml-2' />
                </div>

                <div className='mt-4'>
                  {restaurants?.length > 0 ? (
                    restaurants.map((r, index) => (
                      <RestaurantCard
                        key={index}
                        lastElementRef={lastElementRef}
                        index={index}
                        id={r.id}
                        logoUrl={r.logoUrl}
                        name={r.name}
                        cityName={r.city.name}
                        onRestaurantClick={() => onRestaurantClick(r)}
                      />
                    ))
                  ) : !loadingRestaurants && restaurants?.length === 0 ? (
                    <div className='mt-3'>
                      <EmptyActivity
                        title='Lo sentimos'
                        subtitle='En este momento no hay locales disponibles'
                      />
                    </div>
                  ) : (
                    skeletonRestaurantCard()
                  )}

                  {loadMoreRestaurants && <div>{skeletonRestaurantCard()}</div>}
                </div>
              </div>
            )}
          </ContentApp>
        </div>

        {!canMakeReservations && (
          <>
            <div className={styles.censuredOverlay}>
              <div className={styles.censuredCard + ' censured-text'}>
                <div className='font-large bold mb-2'>{t('verificationPending')}</div>
                <div style={{ opacity: 0.9 }}>{t('continueWithThisActivity')}</div>
                <div className='mt-4'>
                  <Button
                    type='button'
                    size='small'
                    text={t('verifyAccount')}
                    color='#fff'
                    onClick={() => history.push('/account-verification')}
                  />
                </div>
              </div>
            </div>
            <div className={styles.censuredOverlay} />
          </>
        )}
      </div>
      <CustomerNav history={history} />
    </>
  );
};
export default RestaurantList;
